/**
 * useOpenAipRestrictionLayer — wires an OpenAIP "Restriction drone" layer
 * into the main FlightMap Leaflet instance.
 *
 * Lifecycle:
 *   - First activation → fetch the country-specific GeoJSON, build a
 *     Leaflet GeoJSON layer with colour-coded styling + popups, attach.
 *   - Subsequent toggle on → re-attach the cached layer (no re-fetch).
 *   - Toggle off → detach without destroying the layer (so toggling on
 *     again is instant).
 *
 * Each colour and popup field is derived directly from the backend's
 * `_to_feature(...)` output in `openaip_drone_service.py`, so updating
 * the wire shape in one place automatically propagates to every country
 * using this hook (Italy, Sweden, and future additions).
 *
 * @param {Object}        params
 * @param {boolean}       params.enabled      Whether the layer should be active
 * @param {Object}        params.mapInstance  React ref to the Leaflet map
 * @param {Object}        params.layerRef     React ref that will store the L.geoJSON
 * @param {string}        params.country      Country ISO-2 (only used in console logs)
 * @param {string}        params.endpoint     Backend URL path (e.g. /api/openaip/SE/drone-restrictions)
 * @param {number[]}      params.recenter     [lat, lng, zoom] to re-centre on first activation
 */
import { useEffect } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function useOpenAipRestrictionLayer({ enabled, mapInstance, layerRef, country, endpoint, recenter }) {
  useEffect(() => {
    if (!mapInstance.current) return;
    let cancelled = false;

    const detach = () => {
      if (layerRef.current && mapInstance.current.hasLayer(layerRef.current)) {
        mapInstance.current.removeLayer(layerRef.current);
      }
    };

    if (!enabled) {
      detach();
      return;
    }

    const attach = () => {
      if (!layerRef.current) return;
      if (!mapInstance.current.hasLayer(layerRef.current)) {
        layerRef.current.addTo(mapInstance.current);
      }
      if (Array.isArray(recenter) && recenter.length === 3) {
        mapInstance.current.setView([recenter[0], recenter[1]], recenter[2]);
      }
    };

    if (layerRef.current) {
      attach();
      return;
    }

    (async () => {
      try {
        const r = await fetch(`${API_BASE}${endpoint}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const fc = await r.json();
        if (cancelled) return;

        // eslint-disable-next-line no-undef
        const L = window.L;
        if (!L) {
          console.error(`[openaip:${country}] Leaflet (window.L) not available`);
          return;
        }
        const layer = L.geoJSON(fc, {
          style: (feat) => ({
            color: feat.properties.color || '#64748b',
            weight: 1.5,
            fillColor: feat.properties.color || '#64748b',
            fillOpacity: 0.25,
          }),
          onEachFeature: (feat, lyr) => {
            const p = feat.properties || {};
            const html = `
              <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif; min-width:220px">
                <div style="display:flex; align-items:center; gap:6px; margin-bottom:6px">
                  <span style="display:inline-block; width:10px; height:10px; background:${p.color}; border-radius:2px"></span>
                  <strong style="color:#0f172a">${p.name}</strong>
                </div>
                <div style="font-size:12px; color:#475569; margin-bottom:4px">${p.label || p.category}</div>
                <table style="font-size:12px; color:#334155; border-collapse:collapse; width:100%">
                  <tr><td style="padding:2px 8px 2px 0; color:#64748b">Plancher</td><td style="font-weight:600">${p.lower_limit}</td></tr>
                  <tr><td style="padding:2px 8px 2px 0; color:#64748b">Plafond</td><td style="font-weight:600">${p.upper_limit}</td></tr>
                  ${p.by_notam ? '<tr><td colspan="2" style="padding-top:4px; color:#b45309; font-size:11px">⚠️ Activé par NOTAM</td></tr>' : ''}
                  ${p.on_request ? '<tr><td colspan="2" style="padding-top:4px; color:#b45309; font-size:11px">⚠️ Sur demande</td></tr>' : ''}
                </table>
                <div style="margin-top:6px; font-size:10px; color:#94a3b8">Source : OpenAIP</div>
              </div>`;
            lyr.bindPopup(html);
          },
        });
        layerRef.current = layer;
        attach();
      } catch (e) {
        console.error(`[openaip:${country}] load failed`, e);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
